/**
 * draftAutosave: the localStorage draft-with-TTL pattern shared by the note
 * composer and the Assessment Drafter's and Letter's notes fields.
 *
 * Contract:
 *   - drafts store { text, savedAt } under a caller-supplied key, plus one
 *     optional top-level `authorId`: the principal id (`/.auth/me` userId)
 *     of the person who typed it. The field is omitted when the author is
 *     unknown, never written empty, never nested and never part of the key;
 *   - a draft older than DRAFT_TTL_MS is discarded on read (a draft can
 *     carry client narrative and must not persist indefinitely);
 *   - a draft stamped with another author is never restored: the reader
 *     removes it, and with no author given it refuses it and leaves it;
 *   - writing an empty string removes the entry (purge-on-post);
 *   - localStorage being unavailable (private mode) degrades to no-op.
 *
 * Sign-out keeps drafts; sign-in runs `claimDraftsFor` over the store, so a
 * different person signing in on the browser never finds the last one's.
 */

/** Draft lifetime: discarded on read once older than 24h. */
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export const NOTE_DRAFT_PREFIX = 'kith-note-draft';
export const ASSESSMENT_NOTES_DRAFT_PREFIX = 'assessment-notes-draft';
export const ASSESSMENT_LETTER_NOTES_DRAFT_PREFIX = 'assessment-letter-notes-draft';
/** Work kept through an expired sign-in (src/lib/keptWork.ts). */
export const KEPT_WORK_PREFIX = 'kept-work';

/** Every draft key begins with one of these, followed by a colon. */
export const DRAFT_KEY_PREFIXES: readonly string[] = [
  NOTE_DRAFT_PREFIX,
  ASSESSMENT_NOTES_DRAFT_PREFIX,
  ASSESSMENT_LETTER_NOTES_DRAFT_PREFIX,
  KEPT_WORK_PREFIX,
];

export function isDraftKey(key: string): boolean {
  return DRAFT_KEY_PREFIXES.some((prefix) => key.startsWith(`${prefix}:`));
}

interface StoredDraft {
  text: string;
  savedAt: number;
  authorId?: unknown;
}

/** The stored value as a draft, or null when it is not one. Throws on text
 *  that is not JSON. */
function parseDraft(raw: string): StoredDraft | null {
  const parsed: unknown = JSON.parse(raw);
  if (
    parsed !== null &&
    typeof parsed === 'object' &&
    typeof (parsed as StoredDraft).text === 'string' &&
    typeof (parsed as StoredDraft).savedAt === 'number'
  ) {
    return parsed as StoredDraft;
  }
  return null;
}

/**
 * The draft under `key` for `authorId`, or '' when there is none to
 * restore. An unstamped draft is restored to anyone (the caller's next
 * write stamps it). A draft stamped with a different author is removed;
 * with `authorId` null it is refused and left in place.
 */
export function readStoredDraft(key: string, authorId: string | null): string {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return '';
    const draft = parseDraft(raw);
    if (draft === null || Date.now() - draft.savedAt > DRAFT_TTL_MS) {
      // Expired, or not a draft: discard, don't restore.
      window.localStorage.removeItem(key);
      return '';
    }
    if (draft.authorId === undefined || draft.authorId === authorId) return draft.text;
    if (authorId !== null) window.localStorage.removeItem(key);
    return '';
  } catch {
    // Unparseable value or localStorage unavailable (private mode): treat
    // as no draft.
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
    return '';
  }
}

/** Store `value` under `key`, stamped with `authorId` when it is known. */
export function writeStoredDraft(key: string, value: string, authorId: string | null): void {
  try {
    if (value) {
      const draft: StoredDraft = authorId
        ? { text: value, savedAt: Date.now(), authorId }
        : { text: value, savedAt: Date.now() };
      window.localStorage.setItem(key, JSON.stringify(draft));
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // localStorage unavailable (private mode): drafts just don't persist.
  }
}

/**
 * Claim every draft in `storage` for `userId`, at sign-in: a value that is
 * not a draft is removed; an unstamped draft is written back stamped with
 * `userId`, its text and savedAt unchanged; the author's own drafts are
 * left exactly as they are; any other author's are removed. Age is the
 * reader's rule and is not examined here. Keys are collected before any is
 * touched, since removing while walking by index skips keys.
 */
export function claimDraftsFor(userId: string, storage: Storage = window.localStorage): void {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key !== null && isDraftKey(key)) keys.push(key);
  }
  for (const key of keys) {
    const raw = storage.getItem(key);
    if (raw === null) continue;
    let draft: StoredDraft | null;
    try {
      draft = parseDraft(raw);
    } catch {
      draft = null;
    }
    if (draft === null) {
      storage.removeItem(key);
    } else if (draft.authorId === undefined) {
      storage.setItem(key, JSON.stringify({ ...draft, authorId: userId }));
    } else if (draft.authorId !== userId) {
      storage.removeItem(key);
    }
  }
}
